import type { ItemFieldEntry } from '@cheqroom/api';
import { Children, FC, isValidElement, PropsWithChildren, ReactNode } from 'react';

import { Skeleton } from '../../Skeleton';
import { HStack, type StackProps } from '../../Stack';
import { AutoRenderedFields } from './AutoRenderedFields';
import { Field } from './Field';
import { Fields } from './Fields';
import type { FieldRenderOptions, InfoField } from './Fields.types';
import { collectFieldOverrides } from './utils';

export interface InfoFieldsProps extends StackProps {
	fieldDefs?: ItemFieldEntry[];
	fields: InfoField[];
	extraFields?: InfoField[];
	isLoading?: boolean;
	renderField?: (field: InfoField, options: FieldRenderOptions) => ReactNode;
}

export const InfoFields: FC<PropsWithChildren<InfoFieldsProps>> = ({
	fieldDefs,
	fields,
	extraFields,
	isLoading = false,
	renderField,
	children,
	align = 'center',
	justify,
	spacing = 'tight',
	wrap = true,
	className,
}) => {
	const stackProps = { align, justify, spacing, wrap, className };

	if (isLoading) {
		// An extra field replaces a same-id field def, so each id counts once.
		const skeletonCount = new Set([...(fieldDefs ?? []), ...(extraFields ?? [])].map(({ id }) => id)).size || 3;
		return (
			<HStack {...stackProps}>
				{Array.from({ length: skeletonCount }, (_, index) => (
					// eslint-disable-next-line react/no-array-index-key -- fixed-length placeholder list, never reordered
					<Skeleton key={index} width="60px" height="16px" />
				))}
			</HStack>
		);
	}

	const overrides = collectFieldOverrides(children);
	// `Field` elements are pure override config, never rendered as themselves.
	const childArray = Children.toArray(children).filter((child) => !(isValidElement(child) && child.type === Field));
	const renderFields = (key?: string) => (
		<AutoRenderedFields
			key={key}
			fields={fields}
			overrides={overrides}
			extraFields={extraFields}
			renderField={renderField}
		/>
	);

	// With no real children (only Field overrides, or nothing at all), auto-render everything —
	// the zero-config case. Otherwise, children fully control the layout: the auto-rendered list
	// only appears where an explicit `Fields` marker is placed, so consumers can order it
	// relative to the Info type's other members themselves.
	const content: ReactNode =
		childArray.length === 0
			? renderFields()
			: childArray.map((child, index) =>
					isValidElement(child) && child.type === Fields
						? // position among a stable, author-written children list
							renderFields(`fields-${index}`)
						: child
				);

	return <HStack {...stackProps}>{content}</HStack>;
};
